import { useCallback } from 'react';
import { MEDIA_QUERIES } from 'shared/styles/variables';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import { BillingInfo } from 'dashboard/models/organization';
import AddBillingInfoModal from 'dashboard/views/DashboardOrganizationBillingPaymentView/AddBillingInfoModal';
import CurrentCardInfo from 'dashboard/views/DashboardOrganizationBillingPaymentView/CurrentCardInfo';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { Paragraph } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

interface DashboardOrganizationBillingPaymentViewProps {
  currentPaymentMethodDetails: BillingInfo | null;
  showMessage: (message: string) => void;
  updatePaymentMethod: (stripeId: string) => Promise<void>;
}

const DashboardOrganizationBillingPaymentView = ({
  currentPaymentMethodDetails,
  showMessage,
  updatePaymentMethod,
}: DashboardOrganizationBillingPaymentViewProps) => {
  const hasPermission = usePermission(Permission.OrganizationWrite);

  const [
    showAddBillingInfoModal,
    { enable: openAddBillingInfoModal, disable: hideAddBillingInfoModal },
  ] = useToggle(false);

  const handleSubmitBillingInfoAndClose = useCallback(
    async (stripeId: string) => {
      await updatePaymentMethod(stripeId);
      hideAddBillingInfoModal();
    },
    [updatePaymentMethod, hideAddBillingInfoModal],
  );

  return (
    <>
      <ModalWrapper isOpen={showAddBillingInfoModal} handleCloseModal={hideAddBillingInfoModal}>
        <AddBillingInfoModal
          handleClose={hideAddBillingInfoModal}
          handleSubmitBillingInfo={handleSubmitBillingInfoAndClose}
          showMessage={showMessage}
        />
      </ModalWrapper>
      <OrganizationCard>
        {currentPaymentMethodDetails ? (
          <>
            <Card.Body>
              <Card.Title>Payment method</Card.Title>
              <CurrentCardInfo billingInfo={currentPaymentMethodDetails} />
            </Card.Body>
            <ButtonSection>
              <Button
                id='organization__add-billing-info-button'
                data-test='organization__add-billing-info-button'
                disabled={!hasPermission}
                onClick={openAddBillingInfoModal}
                width='button'
              >
                Update payment method
              </Button>
            </ButtonSection>
          </>
        ) : (
          <>
            <Card.Body>
              <Card.Title>Payment method</Card.Title>
              <Paragraph>Add a payment method to your account</Paragraph>
            </Card.Body>
            <ButtonSection>
              <Button
                id='organization__add-billing-info-button'
                data-test='organization__add-billing-info-button'
                disabled={!hasPermission}
                onClick={openAddBillingInfoModal}
                width='button'
              >
                Add payment method
              </Button>
            </ButtonSection>
          </>
        )}
      </OrganizationCard>
    </>
  );
};

const OrganizationCard = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;

  @media ${MEDIA_QUERIES.isMobile} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const ButtonSection = styled(Card.Body)`
  display: flex;
  align-items: top;
`;

export default DashboardOrganizationBillingPaymentView;
